'use client'

import { useState } from 'react'
import Image from 'next/image'
import { PiPencilSimple } from 'react-icons/pi'
import HowToBuyEditModal from './HowToBuyEditModal'
import { resolveChannelIcon } from '@/lib/salesChannels'

type SalesChannelItem = {
  sales_channel_id: number
  name?: string | null
  description?: string | null
  icon?: string | null
}

type HowToBuyProps = {
  listingId?: number | null
  businessName?: string | null
  salesChannels?: SalesChannelItem[] | null
  isOwner: boolean
  onUpdated?: () => void
}

export default function HowToBuy({
  listingId,
  businessName,
  salesChannels,
  isOwner,
  onUpdated,
}: HowToBuyProps) {
  const [editOpen, setEditOpen] = useState(false)
  const channels = salesChannels || []

  // Visitors with no channels: hide. Owners always see the box so they can edit.
  if (!isOwner && channels.length === 0) return null

  return (
    <>
      <div className="flex w-full min-w-0 flex-col items-start gap-3 rounded-xl bg-[#F2F9E9] p-3 sm:p-3">
        <div className="flex w-full min-w-0 items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
            <h3 className="w-full font-inter text-base font-semibold leading-6 tracking-[-0.01em] text-[#202020]">
              How to Buy
            </h3>
            {channels.length > 0 ? (
              <p className="w-full break-words font-inter text-sm font-normal leading-[150%] tracking-[-0.01em] text-[#202020]">
                Shop with {businessName || 'this producer'} through these channels
              </p>
            ) : (
              <p className="w-full font-inter text-sm font-normal leading-[150%] tracking-[-0.01em] text-[#202020]">
                Let customers know how they can buy from you.
              </p>
            )}
          </div>
          {isOwner && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-white sm:h-[38px] sm:w-[38px]"
              aria-label="Edit how to buy"
            >
              <PiPencilSimple size={20} className="text-[#3978D7] sm:hidden" />
              <PiPencilSimple size={24} className="hidden text-[#3978D7] sm:block" />
            </button>
          )}
        </div>

        {channels.length > 0 && (
          <div className="flex w-full min-w-0 flex-row flex-wrap items-start gap-2">
            {channels.map((channel) => {
              const icon = resolveChannelIcon(channel.name, channel.icon)
              return (
                <div
                  key={channel.sales_channel_id}
                  className="flex max-w-full items-center gap-1.5 rounded-full border border-[rgba(133,195,73,0.4)] bg-white px-2.5 py-1.5 sm:h-8 sm:py-2"
                >
                  {icon && (
                    <Image
                      src={icon}
                      alt=""
                      width={16}
                      height={16}
                      className="h-4 w-4 flex-shrink-0 object-contain"
                      unoptimized
                    />
                  )}
                  <span className="truncate font-inter text-[13px] font-semibold leading-4 text-[#548828]">
                    {channel.name}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {isOwner && listingId && (
        <HowToBuyEditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          listingId={listingId}
          selected={channels}
          onSaved={() => {
            setEditOpen(false)
            onUpdated?.()
          }}
        />
      )}
    </>
  )
}
